import type { BlockDefinition, LiveStatusProps, LiveStatusVariant } from '../types';
import { escapeHtml } from '../html';

/**
 * Live status pill — a small designed indicator that broadcasts the
 * creator's current availability or stream state. Always renders a
 * pulsing dot to draw the eye; variant decides the color + default copy:
 *
 *   - 'online' (green)  — "Online now"     — pulses gently
 *   - 'live'   (red)    — "🔴 LIVE NOW"     — pulses faster + uppercase
 *   - 'away'   (amber)  — "Away"           — slow pulse
 *   - 'busy'   (orange) — "Busy"           — slow pulse
 *
 * Customer can override the label freely (e.g. "Streaming on Twitch",
 * "Be back at 5pm EST"). Pure HTML+CSS, no JS — the pulse is a CSS
 * animation.
 */
export const liveStatus: BlockDefinition<LiveStatusProps> = {
  type: 'live-status',
  meta: {
    label: 'Live status',
    icon: '🟢',
    category: 'content',
    description: 'Pulsing availability or live-stream indicator.',
  },
  defaults: () => ({
    label: 'Online now',
    variant: 'online',
    align: 'center',
  }),
  validate(input) {
    const i = (input ?? {}) as Partial<LiveStatusProps>;
    const variant: LiveStatusVariant = (
      ['online', 'live', 'away', 'busy'] as const
    ).includes(i.variant as LiveStatusVariant)
      ? (i.variant as LiveStatusVariant)
      : 'online';
    return {
      label: typeof i.label === 'string' ? i.label.slice(0, 80) : '',
      variant,
      align: ['left', 'center', 'right'].includes(i.align as string)
        ? (i.align as LiveStatusProps['align'])
        : 'center',
    };
  },
  render(props) {
    const justify =
      props.align === 'left' ? 'flex-start' : props.align === 'right' ? 'flex-end' : 'center';

    // Variant palette — each picks a dot color, a soft pill background
    // (low-opacity tint of the dot), an outer ring color, and a label
    // text color tuned for both light + dark themes.
    const palette: Record<
      LiveStatusVariant,
      { dot: string; bg: string; ring: string; text: string; uppercase: boolean; speed: string }
    > = {
      online: {
        dot: '#22c55e',
        bg: 'rgba(34,197,94,0.12)',
        ring: 'rgba(34,197,94,0.32)',
        text: '#16a34a',
        uppercase: false,
        speed: '1.8s',
      },
      live: {
        dot: '#ef4444',
        bg: 'rgba(239,68,68,0.12)',
        ring: 'rgba(239,68,68,0.32)',
        text: '#dc2626',
        uppercase: true,
        speed: '1.1s',
      },
      away: {
        dot: '#f59e0b',
        bg: 'rgba(245,158,11,0.12)',
        ring: 'rgba(245,158,11,0.32)',
        text: '#b45309',
        uppercase: false,
        speed: '2.6s',
      },
      busy: {
        dot: '#fb923c',
        bg: 'rgba(251,146,60,0.12)',
        ring: 'rgba(251,146,60,0.32)',
        text: '#c2410c',
        uppercase: false,
        speed: '2.6s',
      },
    };
    const p = palette[props.variant];

    const safeId = 'pb_ls_' + Math.random().toString(36).slice(2, 10);
    const text = escapeHtml(props.label || '');
    const textTransform = p.uppercase ? 'uppercase' : 'none';
    const letterSpacing = p.uppercase ? '0.06em' : '0';

    return `<div class="pb-block pb-live-status" style="display:flex;justify-content:${justify};">
  <span style="display:inline-flex;align-items:center;gap:8px;padding:6px 14px 6px 12px;background:${p.bg};color:${p.text};border-radius:9999px;font-size:13px;font-weight:600;text-transform:${textTransform};letter-spacing:${letterSpacing};box-shadow:0 0 0 1px ${p.ring};">
    <span aria-hidden="true" style="position:relative;display:inline-flex;align-items:center;justify-content:center;width:8px;height:8px;flex-shrink:0;">
      <span style="position:absolute;inset:0;background:${p.dot};border-radius:9999px;animation:${safeId}_pulse ${p.speed} ease-out infinite;opacity:0.6;"></span>
      <span style="position:relative;width:8px;height:8px;background:${p.dot};border-radius:9999px;"></span>
    </span>
    <span>${text}</span>
  </span>
</div>
<style>@keyframes ${safeId}_pulse{0%{transform:scale(1);opacity:0.6;}80%{transform:scale(2.4);opacity:0;}100%{transform:scale(2.4);opacity:0;}}</style>`;
  },
};
